<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CompanyAPI from 'dashboard/api/companies';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CompanyFormDialog from 'dashboard/components-next/Companies/CompanyFormDialog.vue';
import ContactPickerDialog from 'dashboard/components-next/Contacts/ContactPickerDialog.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const companyId = computed(() => Number(route.params.companyId));

const company = ref(null);
const contacts = ref([]);
const totalContacts = ref(0);
const page = ref(1);
const isFetchingContacts = ref(false);
const companyFormDialogRef = ref(null);
const contactPickerRef = ref(null);
const removeDialogRef = ref(null);
const contactToRemove = ref(null);

const hasMoreContacts = computed(
  () => contacts.value.length < totalContacts.value
);

const fetchCompany = async () => {
  const { data } = await CompanyAPI.show(companyId.value);
  company.value = data.payload;
};

const fetchContacts = async (nextPage = 1) => {
  isFetchingContacts.value = true;
  try {
    const { data } = await CompanyAPI.getContacts(companyId.value, nextPage);
    contacts.value =
      nextPage === 1 ? data.payload : [...contacts.value, ...data.payload];
    totalContacts.value = data.meta.total_count;
    page.value = nextPage;
  } finally {
    isFetchingContacts.value = false;
  }
};

const refresh = () => Promise.all([fetchCompany(), fetchContacts()]);

const addContacts = async selectedContacts => {
  try {
    await CompanyAPI.addContacts(
      companyId.value,
      selectedContacts.map(contact => contact.id)
    );
    useAlert(t('COMPANIES.DETAILS.ADD_SUCCESS'));
    await refresh();
  } catch (error) {
    useAlert(t('COMPANIES.DETAILS.ADD_ERROR'));
  }
};

const confirmRemoveContact = contact => {
  contactToRemove.value = contact;
  removeDialogRef.value?.open();
};

const removeContact = async () => {
  try {
    await CompanyAPI.removeContact(companyId.value, contactToRemove.value.id);
    removeDialogRef.value?.close();
    useAlert(t('COMPANIES.DETAILS.REMOVE_SUCCESS'));
    await refresh();
  } catch (error) {
    useAlert(t('COMPANIES.DETAILS.REMOVE_ERROR'));
  }
};

const openContact = contactId =>
  router.push({ name: 'contact_profile_dashboard', params: { contactId } });

const goBack = () => router.push({ name: 'companies_dashboard_index' });

onMounted(refresh);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="flex flex-col w-full gap-6 px-6 py-6 mx-auto max-w-[60rem]">
      <Button
        :label="t('COMPANIES.DETAILS.BACK')"
        icon="i-lucide-arrow-left"
        variant="link"
        color="slate"
        size="sm"
        class="self-start"
        @click="goBack"
      />

      <div v-if="company" class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-4 min-w-0">
          <Avatar
            :name="company.name"
            :src="company.avatar_url"
            :size="48"
            rounded-full
          />
          <div class="flex flex-col gap-1 min-w-0">
            <span class="text-xl font-medium truncate text-n-slate-12">
              {{ company.name }}
            </span>
            <span v-if="company.domain" class="text-sm text-n-slate-11">
              {{ company.domain }}
            </span>
            <span v-if="company.description" class="text-sm text-n-slate-11">
              {{ company.description }}
            </span>
          </div>
        </div>
        <Button
          :label="t('COMPANIES.DETAILS.EDIT')"
          icon="i-lucide-pencil"
          variant="outline"
          color="slate"
          size="sm"
          @click="companyFormDialogRef?.open(company)"
        />
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <span class="text-base font-medium text-n-slate-12">
            {{ t('COMPANIES.DETAILS.CONTACTS_TITLE', { n: totalContacts }) }}
          </span>
          <Button
            :label="t('COMPANIES.DETAILS.ADD_CONTACT')"
            icon="i-lucide-user-plus"
            size="sm"
            @click="contactPickerRef?.open()"
          />
        </div>

        <div
          v-if="!contacts.length && !isFetchingContacts"
          class="p-4 text-sm border border-dashed rounded-md border-n-weak text-n-slate-11"
        >
          {{ t('COMPANIES.DETAILS.EMPTY_CONTACTS') }}
        </div>

        <div
          v-for="contact in contacts"
          :key="contact.id"
          class="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
        >
          <button
            type="button"
            class="flex items-center gap-3 min-w-0 text-left"
            @click="openContact(contact.id)"
          >
            <Avatar :name="contact.name" :src="contact.thumbnail" :size="32" />
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ contact.name }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ contact.phone_number || contact.email }}
              </span>
            </div>
          </button>
          <Button
            v-tooltip.top="t('COMPANIES.DETAILS.REMOVE_CONTACT')"
            icon="i-lucide-user-minus"
            variant="ghost"
            color="ruby"
            size="sm"
            @click="confirmRemoveContact(contact)"
          />
        </div>

        <div v-if="isFetchingContacts" class="flex justify-center p-2">
          <Spinner />
        </div>
        <Button
          v-else-if="hasMoreContacts"
          :label="t('COMPANIES.DETAILS.LOAD_MORE')"
          variant="ghost"
          color="slate"
          size="sm"
          class="self-center"
          @click="fetchContacts(page + 1)"
        />
      </div>
    </div>

    <ContactPickerDialog
      ref="contactPickerRef"
      multiple
      :title="t('COMPANIES.DETAILS.ADD_CONTACT')"
      @confirm="addContacts"
    />
    <Dialog
      ref="removeDialogRef"
      type="alert"
      :title="t('COMPANIES.DETAILS.REMOVE_CONFIRM_TITLE')"
      :description="
        t('COMPANIES.DETAILS.REMOVE_CONFIRM_DESCRIPTION', {
          contact: contactToRemove?.name,
          company: company?.name,
        })
      "
      :confirm-button-label="t('COMPANIES.DETAILS.REMOVE_CONTACT')"
      @confirm="removeContact"
    />
    <CompanyFormDialog
      ref="companyFormDialogRef"
      @saved="fetchCompany"
      @deleted="goBack"
    />
  </section>
</template>
